import React, {useState, useEffect, useRef} from "react";
import {useObserver} from "mobx-react-lite";
import {Row, Col} from "antd";
import {useStore} from "../../hooks/useStore";
import {useFetch} from "../../hooks/useFetch";
import useIsFocused from "../../hooks/useIsFocused";
import {useTranslation} from "react-i18next";

import {Container, SelectField} from "./statisticsStyles";
import {cities} from "../../constants/constants";
import {arrayAlphabeticalSort} from "../../utils/helpers";
import {Statistic} from "../statistics/Statistic";
import {CityFilter, CompanyFilter} from "../_defaultComponents/FilterComponents";

export const Statistics = () => {
    const stores = useStore();
    const {t} = useTranslation(["statisticsPage"]);
    const isFocused = useIsFocused();
    const [recall, setRecall] = useState(true);
    const [startDateQueueStatistic, setStartDateQueueStatistic] = useState("");
    const [endDateQueueStatistics, setEndDateQueueStatistics] = useState("");
    const [startDateOrderStatistics, setStartDateOrderStatistics] = useState("");
    const [endDateOrderStatistics, setEndDateOrderStatistics] = useState("");
    const [startDateMergeStatistics, setStartDateMergeStatistics] = useState("");
    const [endDateMergeStatistics, setEndDateMergeStatistics] = useState("");
    const [minuteCurrent, setMinuteCurrent] = useState(45);
    const [minuteHistoric, setMinuteHistoric] = useState(45);
    const [dateTypeForOrderStatistics, setDateTypeForOrderStatistics] = useState("daily");
    const [dateTypeForQueueStatistics, setDateTypeForQueueStatistics] = useState("daily");
    const [dateTypeForMergeStatistics, setDateTypeForMergeStatistics] = useState("daily");
    const [dependencyCondition, setDependencyCondition] = useState(true);
    const [city, setCity] = useState("Tümü");
    const [companyFilter, setCompanyFilter] = useState("");

    const recallRef = useRef(recall);
    recallRef.current = recall;

    const [
        currentStatisticsLoading,
        currentStatistics,
        setCurrentStatistics,
        setCurrentStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getCurrentStatistics.bind(null, {
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city
        }),
        dependencies: [recall, companyFilter, city],
        initialCall: true
    });

    const [
        historyStatisticsLoading,
        historyStatistics,
        setHistoricStatistics,
        setHistoricStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getHistoricStatistics.bind(null, {
            period: dateTypeForOrderStatistics,
            startDate: startDateOrderStatistics,
            endDate: endDateOrderStatistics,
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city
        }),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            companyFilter,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [
        queueStatisticsLoading,
        queueStatistics,
        setQueueStatistics,
        setQueueStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getQueueStatistics.bind(null, {
            period: dateTypeForQueueStatistics,
            startDate: startDateQueueStatistic,
            endDate: endDateQueueStatistics,
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city
        }),
        dependencies: [
            dateTypeForQueueStatistics,
            startDateQueueStatistic,
            endDateQueueStatistics,
            companyFilter,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [
        mergedOrderStatisticsLoading,
        mergedOrderStatistics,
        setMergeOrderStatistics,
        setMergeOrderStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getMergedOrderStatistics.bind(null, {
            period: dateTypeForMergeStatistics,
            startDate: startDateMergeStatistics,
            endDate: endDateMergeStatistics,
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city
        }),
        dependencies: [
            dateTypeForMergeStatistics,
            startDateMergeStatistics,
            endDateMergeStatistics,
            companyFilter,
            city
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [
        durationStatisticsLoading,
        durationStatistics,
        setDurationStatistics,
        setDurationStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getDurationStatistics.bind(null, {
            period: dateTypeForOrderStatistics,
            startDate: startDateOrderStatistics,
            endDate: endDateOrderStatistics,
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city,
            minute: minuteHistoric
        }),
        dependencies: [
            dateTypeForOrderStatistics,
            startDateOrderStatistics,
            endDateOrderStatistics,
            companyFilter,
            city,
            minuteHistoric
        ],
        initialCall: true,
        condition: dependencyCondition
    });

    const [
        currentDurationStatisticsLoading,
        currentDurationStatistics,
        setCurrentDurationStatistics,
        setCurrentDurationStatisticsLoading
    ] = useFetch({
        fetchFn: stores.statisticStore.getDurationStatistics.bind(null, {
            period: "current",
            startDate: "",
            endDate: "",
            companyId: companyFilter,
            city: city === "Tümü" ? "" : city,
            minute: minuteCurrent
        }),
        dependencies: [companyFilter, city, minuteCurrent],
        initialCall: true
    });

    useEffect(() => {
        if (isFocused) {
            let timeout = null;

            timeout = setTimeout(async () => {
                await setRecall(!recallRef.current);
            }, 30000);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    return useObserver(() => (
        <div data-testid="statisticsScreen">
            <Container>
                <Row justify="start" data-testid="filterCard">
                    <Col xs={24} sm={16} md={12} lg={8} xl={4}>
                        <CompanyFilter
                            showAllOption={true}
                            showPool={false}
                            value={companyFilter}
                            onChange={value => setCompanyFilter(value)}
                        />
                    </Col>
                    <Col xs={24} sm={16} md={12} lg={8} xl={4}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCity(value);
                            }}
                        />
                    </Col>
                </Row>
                {/* <div data-testid="instantData">
                    <Statistic
                        title={t("statisticsPage.fields.instantData", {ns: "statisticsPage"})}
                        dataSource={{...currentStatistics, ...currentDurationStatistics}}
                        minute={minuteCurrent}
                        loading={currentStatisticsLoading}
                        progressLoading={currentDurationStatisticsLoading}
                        type={"current"}
                        setPercentage={value => {
                            setMinuteCurrent(parseInt(value));
                        }}
                    />
                </div> */}
                <div data-testid="deliveryData">
                    <Statistic
                        title={t("statisticsPage.fields.deliveryData", {ns: "statisticsPage"})}
                        dateType={dateTypeForOrderStatistics}
                        dataSource={{...historyStatistics, ...durationStatistics}}
                        minute={minuteHistoric}
                        loading={historyStatisticsLoading}
                        progressLoading={durationStatisticsLoading}
                        type={"historic"}
                        selectFieldOnChange={value => {
                            setDependencyCondition(true);
                            setStartDateOrderStatistics("");
                            setEndDateOrderStatistics("");
                            setDateTypeForOrderStatistics(value);
                            if (value === "choose") {
                                setDependencyCondition(false);
                            }
                        }}
                        rangePickerOnChange={formatString => {
                            setDependencyCondition(true);
                            setStartDateOrderStatistics(formatString[0]);
                            setEndDateOrderStatistics(formatString[1]);
                            setDependencyCondition(true);
                        }}
                        setPercentage={value => {
                            setMinuteHistoric(parseInt(value));
                        }}
                    />
                </div>
                {/* <div data-testid="queueData">
                    <Statistic
                        title={t("statisticsPage.fields.queueData", {ns: "statisticsPage"})}
                        dateType={dateTypeForQueueStatistics}
                        dataSource={{...queueStatistics}}
                        loading={queueStatisticsLoading}
                        type={"queue"}
                        selectFieldOnChange={value => {
                            setStartDateQueueStatistic("");
                            setEndDateQueueStatistics("");
                            setDependencyCondition(true);
                            setDateTypeForQueueStatistics(value);
                            if (value === "choose") {
                                setDependencyCondition(false);
                            }
                        }}
                        rangePickerOnChange={formatString => {
                            setStartDateQueueStatistic(formatString[0]);
                            setEndDateQueueStatistics(formatString[1]);
                            setDependencyCondition(true);
                        }}
                    />
                </div> */}
                {/* <div data-testid="orderMergeData">
                    <Statistic
                        title={t("statisticsPage.fields.orderMergeData", {ns: "statisticsPage"})}
                        dateType={dateTypeForMergeStatistics}
                        dataSource={{...mergedOrderStatistics}}
                        loading={mergedOrderStatisticsLoading}
                        type={"merged"}
                        selectFieldOnChange={value => {
                            setStartDateMergeStatistics("");
                            setEndDateMergeStatistics("");
                            setDependencyCondition(true);
                            setDateTypeForMergeStatistics(value);
                            if (value === "choose") {
                                setDependencyCondition(false);
                            }
                        }}
                        rangePickerOnChange={formatString => {
                            setStartDateMergeStatistics(formatString[0]);
                            setEndDateMergeStatistics(formatString[1]);
                            setDependencyCondition(true);
                        }}
                    />
                </div> */}
            </Container>
        </div>
    ));
};
